import React, { createContext, ReactNode, useEffect, useContext } from 'react';
import LogoIcon from '../components/logo/LogoIcon';
import LogoMain from '../components/logo/LogoMain';
import useLocalStorage from 'hooks/useLocalStorage';
import config from 'config';
import { CustomizationProps, FontFamily, I18n, MenuOrientation, PresetColor, ThemeDirection, ThemeMode } from 'types/config';

// ------------------- TYPES -------------------
interface TenantConfig {
  faviconUrl: string;
  businessLogo: string | null;
  miniBusinessLogo: string | null;
  websiteTitle: string;
  copyrightStatement: string;
  isLoading: boolean;
}

type CombinedConfig = CustomizationProps & TenantConfig;

// ------------------- CONTEXT -------------------
const initialTenantConfig: TenantConfig = {
  faviconUrl: '',
  businessLogo: null,
  miniBusinessLogo: null,
  websiteTitle: '',
  copyrightStatement: '',
  isLoading: true
};

const initialState: CombinedConfig = {
  ...config,
  ...initialTenantConfig,
  onChangeContainer: () => {},
  onChangeLocalization: (lang: I18n) => {},
  onChangeMode: (mode: ThemeMode) => {},
  onChangePresetColor: (theme: PresetColor) => {},
  onChangeDirection: (direction: ThemeDirection) => {},
  onChangeMiniDrawer: (miniDrawer: boolean) => {},
  onChangeMenuOrientation: (menuOrientation: MenuOrientation) => {},
  onChangeFontFamily: (fontFamily: FontFamily) => {}
};

const ConfigContext = createContext<CombinedConfig>(initialState);


type ConfigProviderProps = { children: ReactNode };

function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useLocalStorage('gridfunding-theme-config', initialState);
  const onChangeContainer = () => {
    setConfig({
      ...config,
      container: !config.container
    });
  };

  const onChangeLocalization = (lang: I18n) => {
    setConfig({
      ...config,
      i18n: lang
    });
  };

  const onChangeMode = (mode: ThemeMode) => {
    setConfig({
      ...config,
      mode
    });
  };

  const onChangePresetColor = (theme: PresetColor) => {
    setConfig({
      ...config,
      presetColor: theme
    });
  };

  const onChangeDirection = (direction: ThemeDirection) => {
    setConfig({
      ...config,
      themeDirection: direction
    });
  };

  const onChangeMiniDrawer = (miniDrawer: boolean) => {
    setConfig({
      ...config,
      miniDrawer: miniDrawer
    });
  };

  const onChangeMenuOrientation = (layout: MenuOrientation) => {
    setConfig({
      ...config,
      menuOrientation: layout
    });
  };

  const onChangeFontFamily = (fontFamily: FontFamily) => {
    setConfig({
      ...config,
      fontFamily
    });
  };

  // ---------- TENANT CONFIG ----------
  const domain = window.location.hostname;
  useEffect(() => {
    const fetchTenantConfig = async () => {
      try {
        const apiRoot = import.meta.env.VITE_APP_API_ROOT;
        const apiUrl = `${apiRoot}tenant/configuration`;
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        const gridFundingFavicon = '/favicon.ico';
        const faviconUrl = data.favicon ?? gridFundingFavicon;
        if (data.websiteTitle) document.title = data.websiteTitle;
        if (faviconUrl) {
          document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());
          const link = document.createElement('link');
          link.rel = 'icon';
          link.href = faviconUrl;
          document.head.appendChild(link);
        }

        setConfig((prev) => ({
          ...prev,
          faviconUrl,
          businessLogo: data.businessLogo || null,
          miniBusinessLogo: data.miniBusinessLogo || null,
          websiteTitle: data.websiteTitle || '',
          copyrightStatement: data.copyrightStatement || '',
          isLoading: false
        }));
      } catch (err) {
        setConfig((prev) => ({
          ...prev,
          faviconUrl: '',
          businessLogo: null,
          miniBusinessLogo: null,
          websiteTitle: '',
          copyrightStatement: '',
          isLoading: false
        }));
      }
    };

    fetchTenantConfig();
  }, [domain]);

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeMenuOrientation,
        onChangeFontFamily
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

const useAppConfig = () => useContext(ConfigContext);
export { ConfigProvider, ConfigContext, useAppConfig };
